/**
 * Where the buyer was in the results list, so Back from a listing returns them
 * to it.
 *
 * Browsers restore **document** scroll across history entries. The search
 * results are an inner `overflow-y-auto` pane, and no browser restores those.
 * Measured on the deployed site: San Jose (1,935 homes), list scrolled to
 * `scrollTop: 2400` — the ninth card — then open a home and press Back, and the
 * pane comes back at **0** with the first card at the top. Every home a buyer
 * opens costs them their place. (W13 R125)
 *
 * Keyed by the *results* identity (filters + sort + page, `view` excluded), so a
 * different search starts at the top and the same search resumes. sessionStorage
 * because the memory should live exactly as long as the tab.
 */
export const LIST_SCROLL_KEY = 'goreal:list-scroll';

/** Most recent keys first; `tops` holds the remembered offsets. */
export interface ListScrollMap {
  order: string[];
  tops: Record<string, number>;
}

export const EMPTY_SCROLL_MAP: ListScrollMap = { order: [], tops: {} };

/** How many searches to remember. Bounded so sessionStorage can't grow forever. */
export const SCROLL_MEMORY_LIMIT = 20;

/** Parse the stored blob, tolerating absent, corrupt or hand-edited data. */
export function parseScrollMap(raw: string | null): ListScrollMap {
  if (!raw) return EMPTY_SCROLL_MAP;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return EMPTY_SCROLL_MAP;
    const { order, tops } = parsed as { order?: unknown; tops?: unknown };
    if (!Array.isArray(order) || typeof tops !== 'object' || tops === null) {
      return EMPTY_SCROLL_MAP;
    }
    const cleanTops: Record<string, number> = {};
    for (const [k, v] of Object.entries(tops as Record<string, unknown>)) {
      if (typeof v === 'number' && Number.isFinite(v) && v >= 0) cleanTops[k] = v;
    }
    return {
      order: order.filter((k): k is string => typeof k === 'string' && k in cleanTops),
      tops: cleanTops,
    };
  } catch {
    return EMPTY_SCROLL_MAP;
  }
}

/**
 * Record a position, moving its key to the front and evicting the oldest beyond
 * the limit. A position of 0 is still recorded — "I was at the top" is a real
 * answer, and dropping it would let a stale offset from an earlier visit win.
 */
export function rememberScroll(
  prev: ListScrollMap,
  key: string,
  top: number,
  limit = SCROLL_MEMORY_LIMIT
): ListScrollMap {
  const clamped = Number.isFinite(top) ? Math.max(0, Math.round(top)) : 0;
  const order = [key, ...prev.order.filter((k) => k !== key)].slice(0, limit);
  const kept = new Set(order);
  const tops: Record<string, number> = { [key]: clamped };
  for (const k of order) {
    if (k !== key && kept.has(k) && k in prev.tops) tops[k] = prev.tops[k];
  }
  return { order, tops };
}

/** The remembered position for a search, or 0 when it has never been seen. */
export function recallScroll(map: ListScrollMap, key: string): number {
  return map.tops[key] ?? 0;
}

/* ---- sessionStorage wrappers (no-ops when storage is unavailable) ---- */

function read(): ListScrollMap {
  try {
    return parseScrollMap(sessionStorage.getItem(LIST_SCROLL_KEY));
  } catch {
    return EMPTY_SCROLL_MAP;
  }
}

export function saveListScroll(key: string, top: number): void {
  try {
    sessionStorage.setItem(LIST_SCROLL_KEY, JSON.stringify(rememberScroll(read(), key, top)));
  } catch {
    // private mode / quota — the list just won't resume; never a crash.
  }
}

export function loadListScroll(key: string): number {
  return recallScroll(read(), key);
}
